<script>
    // Vue 3 component for displaying a Leaflet map with clustering functionality
    import { createApp, h } from 'vue';

    // Leaflet components
    import { LMap, LTileLayer, LMarker, LPopup, LCircleMarker } from '@vue-leaflet/vue-leaflet';
    import L from 'leaflet';
    import 'leaflet.markercluster';

    // Shared functionality
    import mixin from './../mixin';
    import listItem from './listItem.vue';

    export default {
        components: {
            LMap,
            LTileLayer,
            LMarker,
            LPopup,
            LCircleMarker,
            listItem
        },
        mixins: [mixin],
        props: {
            items: {
                type: Array,
                required: true
            }
        },
        data() {
            return {
                markerClusterGroup: null,
                circle: {
                    radius: 6,
                    color: '#f26722',
                    fillOpacity: 1
                },
            };
        },
        computed: {
            locations() {
                if (!this.items || !Array.isArray(this.items)) return [];
                return this.items.map(item => item.mapLocation)
                                 .filter(loc => loc && typeof loc.latitude === 'number' && typeof loc.longitude === 'number')
                                 .map(loc => [loc.latitude, loc.longitude]);
            }
        },
        watch: {
            items: {
                handler() {
                    this.refreshMarkers();
                    this.$refs.map?.leafletObject.fitBounds(this.locations);
                },
                deep: true
            }
        },
        // Lifecycle hooks to initialize the map, handle screen size changes and initialize clustering
        mounted() {
            this.waitForMap(0);
        },
        methods: {
            // Method to initialize the clustering call after the map has been initialized
            waitForMap(retries) {
                this.$nextTick(() => {
                    const map = this.$refs.map?.leafletObject;
                    
                    if (map) {
                        map.fitBounds(this.locations);
                        map.invalidateSize();
                        this.setupClustering();
                    } else if (retries < 20) {
                        setTimeout(() => this.waitForMap(retries + 1), 100);
                    } else {
                        console.warn('Map did not initialize in time.');
                    }
                });
            },
            // Method to check whether clusters are needed for this zoom level or not
            setupClustering() {
                const map = this.$refs.map?.leafletObject;
                if (!map) return;

                if (!this.markerClusterGroup) this.markerClusterGroup = L.markerClusterGroup();
                this.refreshMarkers();
            },
            // Method to refresh and populate the markers for clusters or single data points
            // Because clustering is not native to vue3-leaflet, we use leaflet.markercluster directly with app mounting per marker
            refreshMarkers() {
                const map = this.$refs.map?.leafletObject;
                if (!map || !this.markerClusterGroup) return;

                this.markerClusterGroup.clearLayers();
                map.removeLayer(this.markerClusterGroup);
                
                this.items.forEach(item => {
                    const lat = parseFloat(item.mapLocation?.latitude);
                    const lng = parseFloat(item.mapLocation?.longitude);
                    if (!isNaN(lat) && !isNaN(lng)) {
                        const marker = L.circleMarker([lat, lng], {radius: this.circle.radius, color: this.circle.color, fillOpacity: this.circle.fillOpacity});

                        const popupContent = document.createElement('div');
                        marker.bindPopup(popupContent, { maxWidth: 420 });
                            
                        const app = createApp({
                            render: () => h(listItem, {
                                item
                            })
                        });
                        app.mount(popupContent);

                        this.markerClusterGroup.addLayer(marker);
                    }
                });

                if (!map.hasLayer(this.markerClusterGroup)) map.addLayer(this.markerClusterGroup);
            }
        }
    };
</script>

<template>
    <div class="leaflet">
        <l-map ref="map">
            <l-tile-layer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                          layer-type="base"
                          :options="{ maxZoom: 10 }"
                          name="OpenStreetMap"></l-tile-layer>
        </l-map>
    </div>
</template>